import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import { Box, Button, Typography, useTheme } from '@mui/material'
import React, { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { usePostVerifyCode } from '../../api'
import PlatformError from '../../api/helpers/PlatformError'
import { GenerateCodeResponse } from '../../api/v1/verification/usePostGenerateCode'
import { GenerateCodeButton } from '../../auth'
import BodyPaper from '../../components/Layout/BodyPaper'
import Header from '../../components/Layout/Header'
import { AlertType, useAlert } from '../../providers/alert'
import { useAuth } from '../../providers/auth'
import OneTimePasscodeInput from './OneTimePasscodeInput'

type ParentEmailConfirmationPageProps = {
  id: string
  codeData: GenerateCodeResponse | null
  codeLoading: boolean
  codeError: PlatformError | null
  codeCallFetch: () => void
  codeHeaders: Response['headers'] | null
}

const ParentEmailConfirmationPage: React.FC<ParentEmailConfirmationPageProps> = ({
  id,
  codeData,
  codeLoading,
  codeError,
  codeCallFetch,
  codeHeaders,
}) => {
  const theme = useTheme()
  const [code, setCode] = useState<string>('')
  const [invalidCode, setInvalidCode] = useState(false)
  const { t } = useTranslation('vpc', { keyPrefix: 'confirmEmail' })
  const { data: verifyResponse, loading, error, callFetch } = usePostVerifyCode(id, code)
  const { showAlert } = useAlert()
  const { setAuthToken } = useAuth()

  useEffect(() => {
    if (verifyResponse?.token) {
      showAlert({
        msg: t('verifyCodeButton.success'),
        severity: AlertType.SUCCESS,
        duration: 5000,
      })
      setAuthToken(verifyResponse.token, { username: '', suffix: '' })
    }

    if (error && !verifyResponse) {
      setInvalidCode(true)
    } else {
      setInvalidCode(false)
    }
  }, [error, verifyResponse])

  return (
    <>
      <Header title={t('title')} subtitle={t('subtitle')} />
      <BodyPaper
        data-testid="parent-email-confirmation-page"
        sx={{ textAlign: 'center', pt: '3rem' }}
      >
        <EmailOutlinedIcon
          sx={{ fontSize: 100, color: theme.palette.grey[900] }}
          stroke="white"
          strokeWidth=".75px"
          strokeLinejoin="round"
        />
        <Typography variant="bodyMedium" component="p" sx={{ mb: 3.5 }}>
          {t('instructionsNoEmail')}
        </Typography>
        <Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
              mb: 3.5,
              alignItems: 'center',
            }}
          >
            <OneTimePasscodeInput invalidCode={invalidCode} setCode={setCode} />
          </Box>
          <Box
            justifyContent={'center'}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 2.5,
            }}
          >
            <Button
              data-testid="verify-code-button"
              variant="contained"
              onClick={() => {
                if (!verifyResponse) {
                  callFetch()
                }
              }}
              disabled={loading || !code || code.length < 6}
            >
              <Typography variant="buttonMedium" component="p">
                {t('verifyCodeButton.confirm')}
              </Typography>
            </Button>
            <GenerateCodeButton
              data={codeData}
              loading={codeLoading}
              error={codeError}
              callFetch={codeCallFetch}
              headers={codeHeaders}
            />
          </Box>
        </Box>
      </BodyPaper>
    </>
  )
}

export default ParentEmailConfirmationPage
